/**
 * UranIA PA — Login actions (US-010).
 *
 * Wrap di `UserManager.signinRedirect` con:
 *   - `kc_idp_hint`: Keycloak preseleziona l'IdP federato (SPID/CIE).
 *   - `acr_values`: chiede esplicitamente il LoA richiesto (L2 per SPID
 *     Funzionario, CIE per CIE, L3 per CNS — FR-F1-1, analisi §18.2).
 *   - `prompt: 'login'`: forza un round-trip federato anche se Keycloak ha
 *     una sessione SSO attiva (utile per il `/login` re-entry).
 *
 * NB: niente decodifica JWT qui — è il `/auth/callback` (vedi
 * `routes/auth/callback.tsx`) che chiama `signinRedirectCallback()` e popola
 * Zustand store via `setSession`.
 */

import type { User } from 'oidc-client-ts';

import { loadSpaEnv } from '../env.ts';
import { useAuthStore, type UraniaPrincipal } from './auth-store.ts';
import { getUserManager } from './oidc.ts';
import { principalIsAnyAdmin } from './rbac.ts';

export interface StartFederatedLoginOptions {
  /** Alias Keycloak (es. `spid-testenv2-l2`, `cie-staging`). */
  readonly keycloakAlias: string;
  /** `acr_values` da passare al server (es. `L2`, `CIE`, `L3`). */
  readonly acrValues: string;
  /** Dove Keycloak deve rimandare dopo il consenso. Se omesso, la destinazione
   * è decisa in base al ruolo dopo il login (`defaultLandingFor`). */
  readonly returnTo?: string;
}

/**
 * Avvia il flusso OIDC code+PKCE preselezionando l'IdP federato.
 *
 * NB: `signinRedirect` ritorna una Promise che si risolve solo se NON c'è
 * redirect (errore). In condizioni nominali la pagina lascia il tab.
 */
export async function startFederatedLogin(options: StartFederatedLoginOptions): Promise<void> {
  const manager = getUserManager();
  // Dev override: in ambiente locale (`urania-pa-dev` realm) gli IdP federati
  // SPID/CIE/CNS non sono configurati. Saltiamo `kc_idp_hint` cosi' Keycloak
  // mostra la pagina di login standard username/password con gli utenti mock
  // del realm (admin@dev, funz@dev, ri@dev, auditor@dev).
  const isDevRealm =
    typeof import.meta !== 'undefined' &&
    typeof (import.meta as { env?: { VITE_OIDC_AUTHORITY?: string } }).env?.VITE_OIDC_AUTHORITY ===
      'string' &&
    (import.meta as { env: { VITE_OIDC_AUTHORITY: string } }).env.VITE_OIDC_AUTHORITY.includes(
      'urania-pa-dev',
    );

  const extraQueryParams: Record<string, string> = isDevRealm
    ? // Dev: niente acr_values (il realm dev non ha ACR-to-LoA mapping) ne'
      // kc_idp_hint (SPID/CIE/CNS IdP non configurati). Si mostra la form
      // standard username+password con gli utenti mock del realm.
      { prompt: 'login' }
    : {
        kc_idp_hint: options.keycloakAlias,
        acr_values: options.acrValues,
        prompt: 'login',
      };

  await manager.signinRedirect({
    extraQueryParams,
    // Nessun default qui: se `returnTo` non è esplicito, `completeFederatedLogin`
    // sceglie la destinazione in base al ruolo (`defaultLandingFor`) una volta
    // noto il principal — un default statico a '/dashboard' lo impedirebbe.
    state: options.returnTo === undefined ? undefined : { returnTo: options.returnTo },
  });
}

/**
 * Chiude il round-trip OIDC: chiamata dal route `/auth/callback`.
 *
 * Effetti:
 *   1. `signinRedirectCallback()` valida `state` + `nonce` PKCE; ritorna
 *      l'oggetto `User` (id_token + access_token).
 *   2. Estrae i claim minimi → costruisce `UraniaPrincipal`.
 *   3. Popola `useAuthStore.setSession` (access_token + principal).
 *   4. Ritorna l'URL di destinazione: quello esplicito passato a
 *      `startFederatedLogin`, altrimenti `/console` per platform_admin/
 *      tenant_admin, `/servizi` per gli altri ruoli (`defaultLandingFor`).
 *
 * Throws se la validazione fallisce → il route renderizza un messaggio
 * d'errore italiano e propone "Riprova".
 */
export async function completeFederatedLogin(): Promise<string> {
  const manager = getUserManager();
  const user: User = await manager.signinRedirectCallback();
  const principal = principalFromUser(user);
  useAuthStore.getState().setSession({ accessToken: user.access_token, principal });
  return resolveExplicitReturnTo(user.state) ?? defaultLandingFor(principal);
}

/**
 * Login tecnico di collaudo (`/login/staging`, US-F1-XX): grant diretto
 * Resource Owner Password Credentials contro il token endpoint di Keycloak,
 * SENZA passare da `signinRedirect`/PKCE — questo ambiente non ha gli IdP
 * SPID/CIE/CNS attivi (vedi header del file) e simula l'identità digitale
 * con le credenziali tecniche degli utenti seed del realm (`admin@staging`,
 * `funz@staging`, ecc., vedi `infra/staging/keycloak-realm-urania-staging.json`).
 *
 * Il client `spa-staging` ha `directAccessGrantsEnabled: true` proprio per
 * abilitare questo flusso in staging/collaudo — NON è il flusso usato da
 * `/login/pa` in produzione (code+PKCE via `startFederatedLogin`).
 *
 * NB: la sessione risultante vive solo nello store Zustand (come per il
 * flusso federato, che tiene comunque lo `userStore` in memoria) — niente
 * refresh automatico via `UserManager`, che non è a conoscenza di questo
 * login diretto.
 */
export async function signInWithDirectGrant(
  username: string,
  password: string,
): Promise<UraniaPrincipal> {
  const env = loadSpaEnv();
  const tokenUrl = `${env.oidcAuthority.replace(/\/+$/, '')}/protocol/openid-connect/token`;
  const body = new URLSearchParams({
    grant_type: 'password',
    client_id: env.oidcClientId,
    username,
    password,
    scope: 'openid profile email',
  });

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!response.ok) {
    throw new Error(
      response.status === 401 || response.status === 400
        ? 'Credenziali non valide.'
        : `Accesso non riuscito (HTTP ${response.status}).`,
    );
  }

  const tokens = (await response.json()) as { access_token?: string; id_token?: string };
  if (typeof tokens.access_token !== 'string' || typeof tokens.id_token !== 'string') {
    throw new Error('Risposta del server di autenticazione incompleta.');
  }

  const principal = principalFromClaims(decodeJwtPayload(tokens.id_token));
  useAuthStore.getState().setSession({ accessToken: tokens.access_token, principal });
  return principal;
}

/** Destinazione dopo un login riuscito, in assenza di un `returnTo` esplicito:
 * i ruoli platform_admin/tenant_admin atterrano sull'hub admin (`/admin` —
 * era `/console`, fusione A.5: il cruscotto di piattaforma è ora una
 * sezione dell'hub, `/admin/piattaforma`, non più la destinazione diretta),
 * gli altri su `/servizi` (front end 1 — addendum "Front end: pagina dei
 * servizi e ambiente immersivo" §2: `/servizi` è l'atterraggio, `/dashboard`
 * resta raggiungibile da lì per il lavoro operativo assegnato). */
export function defaultLandingFor(principal: UraniaPrincipal): string {
  return principalIsAnyAdmin(principal) ? '/admin' : '/servizi';
}

/**
 * Chiude il round-trip OIDC silent renew: chiamata dal route
 * `/auth/silent-renew`.
 */
export async function completeSilentRenew(): Promise<void> {
  const manager = getUserManager();
  await manager.signinSilentCallback();
}

/**
 * Logout federato.
 *
 * `signoutRedirect` invia il browser al `end_session_endpoint` di Keycloak;
 * Keycloak chiude la SSO session e ridireziona a
 * `post_logout_redirect_uri`. Prima del redirect, svuotiamo il Zustand store.
 */
export async function startLogout(): Promise<void> {
  useAuthStore.getState().clearSession();
  const manager = getUserManager();
  await manager.signoutRedirect();
}

/**
 * Costruisce `UraniaPrincipal` dai claim del `User` OIDC.
 *
 * NB difensivo: in Fase 1 non verifichiamo la firma del JWT lato SPA — la
 * verifica avviene all'identity-proxy (US-031). I claim sono prelevati da
 * `user.profile` (id_token decoded da oidc-client-ts).
 */
function principalFromUser(user: User): UraniaPrincipal {
  return principalFromClaims(user.profile);
}

type ClaimRecord = Record<string, unknown>;

/** Costruisce `UraniaPrincipal` da un set di claim JWT (id_token), sia che
 * arrivino da `user.profile` (oidc-client-ts) sia da un id_token decodificato
 * a mano (grant diretto, vedi `signInWithDirectGrant`). */
function principalFromClaims(profile: ClaimRecord): UraniaPrincipal {
  const subject = typeof profile.sub === 'string' ? profile.sub : '';
  const tenantId = readStringClaim(profile, 'tenant_id');
  const acr = readStringClaim(profile, 'acr');
  const preferredUsername = readStringClaim(profile, 'preferred_username');
  const roles = readRealmRoles(profile);
  return Object.freeze({
    subject,
    tenantId,
    acr,
    preferredUsername,
    roles: Object.freeze(roles),
  });
}

/** Decodifica (senza verificarne la firma — stesso boundary di
 * `principalFromUser`, vedi nota sopra) il payload di un JWT compatto. */
function decodeJwtPayload(jwt: string): ClaimRecord {
  const segment = jwt.split('.')[1];
  if (segment === undefined) {
    throw new Error('id_token non valido.');
  }
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/').padEnd(
    segment.length + ((4 - (segment.length % 4)) % 4),
    '=',
  );
  return JSON.parse(atob(base64)) as ClaimRecord;
}

function readStringClaim(profile: ClaimRecord, key: string): string | null {
  const value = profile[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readRealmRoles(profile: ClaimRecord): readonly string[] {
  const realmAccess = profile.realm_access;
  if (realmAccess === null || typeof realmAccess !== 'object') {
    return [];
  }
  const roles = (realmAccess as ClaimRecord).roles;
  if (!Array.isArray(roles)) {
    return [];
  }
  return roles.filter((r): r is string => typeof r === 'string');
}

function resolveExplicitReturnTo(state: unknown): string | null {
  if (state !== null && typeof state === 'object' && 'returnTo' in state) {
    const returnTo = (state as { returnTo?: unknown }).returnTo;
    if (typeof returnTo === 'string' && returnTo.startsWith('/')) {
      return returnTo;
    }
  }
  return null;
}
